import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import webSocketService from '@/services/websocket.service';

// Recursively walk a cached value and, for any object holding an `images` array,
// patch the entry whose `entry_id` matches the transcode update. Immutable: the
// SAME reference is returned for any subtree that didn't change, so React only
// re-renders the branch that actually updated.
const patchValue = (value, data) => {
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map(item => {
      const patched = patchValue(item, data);
      if (patched !== item) changed = true;
      return patched;
    });
    return changed ? next : value;
  }

  if (value && typeof value === 'object') {
    let changed = false;
    const next = { ...value };

    // Patch a media array on this object. CRM stores media under `images`,
    // KRAN (parts/quotes/line-items) under `files`, in-stock parts/vehicles
    // under `videos` — same per-entry shape with entry_id/status, so handle all.
    for (const arrKey of ['images', 'files', 'videos']) {
      if (!Array.isArray(value[arrKey])) continue;
      let arrChanged = false;
      const nextArr = value[arrKey].map(item => {
        if (item && item.entry_id === data.entry_id) {
          arrChanged = true;
          const patched = { ...item, status: data.status, media_type: 'video' };
          if (data.status === 'ready') {
            patched.url = data.url;
            patched.poster_url = data.poster_url;
            patched.small_url = data.small_url;
          }
          return patched;
        }
        return item;
      });
      if (arrChanged) {
        next[arrKey] = nextArr;
        changed = true;
      }
    }

    // Recurse into all properties so nested shapes are covered.
    for (const key of Object.keys(value)) {
      const child = value[key];
      const patchedChild = patchValue(child, data);
      if (patchedChild !== child) {
        next[key] = patchedChild;
        changed = true;
      }
    }

    return changed ? next : value;
  }

  return value;
};

// Apply a single transcode update to an arbitrary data tree (e.g. a component's
// local useState value) and return a new tree with the matching entry patched
// in place. Same immutable walker as the cache patch, for views that don't keep
// their data in the React Query cache.
export const applyTranscodeUpdate = (data, update) => patchValue(data, update);

// Surgically patch every cache that might hold the affected media entry, by
// `entry_id`, WITHOUT triggering a refetch. No-op when the entry isn't cached.
export const patchTranscode = (queryClient, data) => {
  if (!data || !data.entry_id) return;
  const updater = (cached) => {
    if (cached === undefined || cached === null) return cached;
    return patchValue(cached, data);
  };
  // Quote detail (['quote', id]) and quotes lists (['quotes', ...]) carry
  // line_items[].images[] and line_items[].vehicle.images[]; the vehicles cache
  // lives under ['contact', id, 'vehicles'] as items[].images[].
  queryClient.setQueriesData({ queryKey: ['quote'] }, updater);
  queryClient.setQueriesData({ queryKey: ['quotes'] }, updater);
  queryClient.setQueriesData({ queryKey: ['contact'] }, updater);
  queryClient.setQueriesData({ queryKey: ['lineItems'] }, updater);
  queryClient.setQueriesData({ queryKey: ['recentQuotesForContact'] }, updater);
  // Returns list (['returns', 'list', filters]) holds
  // items[].return_line_items[].line_item.images[] + .vehicle.images[].
  queryClient.setQueriesData({ queryKey: ['returns'] }, updater);
  // KRAN caches hold media under `files[]` (parts/quotes/contracts/work-orders).
  // List queries key off these prefixes; the singular ['kranContract', id] cache
  // backs the Devize/V3 contract detail panel. Detail pages that keep their entity
  // in local state (KranQuoteDetail, KranWorkOrderDetail, KranContractDetail)
  // subscribe via useTranscodeUpdateHandler instead, since the cache patch can't
  // reach local state.
  queryClient.setQueriesData({ queryKey: ['kranParts'] }, updater);
  queryClient.setQueriesData({ queryKey: ['kranQuotes'] }, updater);
  queryClient.setQueriesData({ queryKey: ['kranContracts'] }, updater);
  queryClient.setQueriesData({ queryKey: ['kranContractsV3'] }, updater);
  queryClient.setQueriesData({ queryKey: ['kranContract'] }, updater);
  queryClient.setQueriesData({ queryKey: ['kranWorkOrders'] }, updater);
  // dezShop fleet vehicles expose images_json as `images[]`; list/detail cache
  // under ['fleet', ...]. (FleetDetail also keeps the vehicle in local state and
  // subscribes via useTranscodeUpdateHandler.)
  queryClient.setQueriesData({ queryKey: ['fleet'] }, updater);
};

// Subscribes once to transcode_update WS pushes and patches the React Query
// cache in place — no polling, no refetch, no spinner.
export const useTranscodeUpdates = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = webSocketService.addTranscodeUpdateListener(
      data => patchTranscode(queryClient, data)
    );
    return unsubscribe;
  }, [queryClient]);
};

// For views that keep their data in LOCAL state instead of the React Query
// cache (e.g. QuoteDetail uses useState + a manual fetch, so the global cache
// patch never reaches it). Subscribes once and always invokes the LATEST
// callback via a ref, so callers can pass an inline arrow without
// re-subscribing every render.
export const useTranscodeUpdateHandler = (onUpdate) => {
  const ref = useRef(onUpdate);
  ref.current = onUpdate;
  useEffect(() => {
    const unsubscribe = webSocketService.addTranscodeUpdateListener((data) => {
      if (ref.current) ref.current(data);
    });
    return unsubscribe;
  }, []);
};

export default useTranscodeUpdates;
